import type { FC, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'

import { cn } from '@/lib/utils'
import { vars } from '@/lib/motion'
import { Photo } from '@/components/generals/photo'
import { StageWash } from '@/components/generals/backdrops'
import { ButtonLink } from '@/components/generals/button-link'
import { routes } from '@/constants/site'

interface ChipSpot {
    photo: PhotoId
    /** Desktop spot and width, as percentages of the stage. */
    left: string
    top: string
    width: string
    /** Phone spot (every print is 19% wide there). */
    mobileLeft: string
    mobileTop: string
    rotate: number
    drift: number
}

const SPOTS: ChipSpot[] = [
    { photo: 'elena', left: '3%', top: '38%', width: '11%', mobileLeft: '5%', mobileTop: '66%', rotate: -8, drift: 17 },
    { photo: 'luca', left: '15%', top: '6%', width: '9%', mobileLeft: '10%', mobileTop: '9%', rotate: -5, drift: 21 },
    { photo: 'sofia', left: '76%', top: '10%', width: '10.5%', mobileLeft: '70%', mobileTop: '12%', rotate: 4, drift: 19 },
    { photo: 'adrian', left: '23%', top: '74%', width: '9.5%', mobileLeft: '40%', mobileTop: '78%', rotate: 5, drift: 23 },
    { photo: 'nora', left: '78%', top: '60%', width: '11.5%', mobileLeft: '74%', mobileTop: '62%', rotate: 7, drift: 25 }
]

/** A small photo print: the face, framed in a paper border. */
const PhotoPrint: FC<{ photo: PhotoId }> = ({ photo }): ReactNode => {
    return (
        <span className='block rounded-[6px] bg-paper p-[6%] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_14px_28px_-14px_rgb(40_38_34/0.45)]'>
            <Photo id={photo} sizes='130px' portrait className='aspect-square w-full rounded-[3px]' />
        </span>
    )
}

interface ClosingCtaProps {
    title?: string
    line?: string
    buttonLabel?: string
    href?: string
    id?: string
}

/** A contained stage with five photo prints of designers drifting in a loose ring around the centre. */
export const ClosingCta: FC<ClosingCtaProps> = ({
    title = "Let's find your next designer",
    line,
    buttonLabel = 'Get started',
    href = routes.getStarted,
    id = 'closing-cta'
}): ReactNode => {
    return (
        <section id={id} aria-labelledby={`${id}-title`} className='section-x relative z-10 bg-background py-14 sm:py-[90px]'>
            <div className='reveal relative mx-auto w-full max-w-[1080px]'>
                <div className='relative isolate flex min-h-[560px] w-full items-center justify-center md:min-h-[clamp(400px,36vw,560px)]'>
                    <StageWash />

                    <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                        {SPOTS.map((spot, index) => (
                            <div
                                key={index}
                                className='absolute left-[var(--ml)] top-[var(--mt)] w-[19%] md:left-[var(--l)] md:top-[var(--t)] md:w-[var(--w)]'
                                style={vars({ '--l': spot.left, '--t': spot.top, '--w': spot.width, '--ml': spot.mobileLeft, '--mt': spot.mobileTop })}
                            >
                                <div
                                    className='drift'
                                    style={vars({ '--drift-duration': `${spot.drift}s`, '--drift-y': `${index % 2 === 0 ? -8 : 8}px`, '--drift-delay': `${-index * 3}s` })}
                                >
                                    <div style={{ rotate: `${spot.rotate}deg` }}>
                                        <PhotoPrint photo={spot.photo} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='relative z-[2] flex flex-col items-center gap-[clamp(28px,3vw,40px)] px-4 text-center'>
                        <div>
                            <h2 id={`${id}-title`} className='type-section md:whitespace-nowrap'>
                                {title}
                            </h2>

                            {line && (
                                <p className={cn('type-lead mx-auto mt-4 max-w-[52ch]')}>
                                    {line}
                                </p>
                            )}
                        </div>

                        <ButtonLink href={href} size='lg'>
                            {buttonLabel}
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </section>
    )
}
